<template>
  <div class="zona-pago mt-4">
    <form id="payment-form-element" @submit.prevent="handlePayment">
      <div class="card pago-container shadow-sm border-0 mx-auto">

        <h5 class="titulo-pago text-center">
          <template v-if="isTrial">🎁 {{ $t('paymentStripe.trialActivate') }}</template>
          <template v-else>🔒 {{ $t('paymentStripe.payWithCard') }}</template>
        </h5>

        <!-- Banner periodo de prueba -->
        <div v-if="isTrial" class="trial-banner">
          <span class="trial-badge">{{ diasGracia === 1 ? $t('paymentStripe.trialDaysSingular') : $t('paymentStripe.trialDaysPlural') }}</span>
          <span class="trial-desc">
            {{ diasGracia }} {{ diasGracia === 1 ? $t('paymentStripe.trialDaysSingular') : $t('paymentStripe.trialDaysPlural') }} · <strong>{{ $t('paymentStripe.trialNoChrge') }}</strong>
          </span>
        </div>

        <!-- Stripe Payment Element (tarjeta + Google Pay + Apple Pay + Link) -->
        <div class="payment-element-wrap mt-3">
          <div id="stripe-payment-element"></div>
        </div>

        <p v-if="paymentError" class="error-stripe mt-2">{{ paymentError }}</p>

        <div class="botones-accion mt-4">
          <button
            type="submit"
            class="btn btn-tipster btn-pagar px-4 w-100"
            :disabled="isProcessing || !elementReady"
          >
            <span v-if="isProcessing" class="spinner-border spinner-border-sm"></span>
            <span v-else-if="mode === 'reactivar'">Reactivar suscripción</span>
            <span v-else-if="mode === 'actualizarpago'">Guardar tarjeta</span>
            <span v-else-if="isTrial">{{ $t('paymentStripe.trialStart') }}</span>
            <span v-else>{{ $t('paymentStripe.pay') }}</span>
          </button>
        </div>

        <p v-if="isTrial" class="trial-note text-center mt-2">
          {{ $t('paymentStripe.trialNote') }}
        </p>

      </div>
    </form>
  </div>
</template>

<script>
import showSwal from "/src/mixins/showSwal.js";

export default {
  props: {
    clientSecret: { type: String, required: true },
    publicKey:    { type: String, required: true },
    email:        { type: String, required: false, default: "" },
    telefono:     { type: String, required: false, default: "" },
    mode:         { type: String, default: "payment" },
    referencia:   { type: String, default: "" },
    diasGracia:    { type: Number, default: 0 },
    locale:        { type: String, default: "auto" },
    pais:          { type: String, default: "" },
    esSuscripcion: { type: Boolean, default: false },
  },
  data() {
    return {
      stripe: null,
      elements: null,
      paymentElement: null,
      isProcessing: false,
      elementReady: false,
      paymentError: null,
    };
  },
  computed: {
    isTrial() {
      return this.mode === "setup";
    },
    isSetupMode() {
      return this.mode === "setup" || this.mode === "actualizarpago";
    },
  },
  emits: ["cerrar-componente"],
  mounted() {
    this.stripe = window.Stripe(this.publicKey);

    const appearance = {
      theme: "night",
      variables: {
        colorPrimary:       "#00c896",
        colorBackground:    "#1a2535",
        colorText:          "#e7edf5",
        colorDanger:        "#ff5964",
        fontFamily:         '"Inter", system-ui, -apple-system, Roboto, Arial, sans-serif',
        borderRadius:       "10px",
        spacingUnit:        "4px",
      },
      rules: {
        ".Input": {
          border:          "1px solid rgba(255,255,255,.12)",
          backgroundColor: "#1a2535",
          color:           "#e7edf5",
        },
        ".Label": {
          color:     "rgba(231,237,245,0.75)",
          fontSize:  "0.88rem",
          fontWeight: "600",
        },
        ".Tab": {
          border:          "1px solid rgba(255,255,255,.12)",
          backgroundColor: "#1a2535",
        },
        ".Tab--selected": {
          borderColor:     "#00c896",
          backgroundColor: "rgba(0,200,150,.08)",
        },
      },
    };

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      appearance,
      locale: this.locale || "auto",
    });

    this.paymentElement = this.elements.create("payment", {
      defaultValues: {
        billingDetails: {
          email: this.email || undefined,
          phone: this.telefono || undefined,
          address: this.pais ? { country: this.pais } : undefined,
        },
      },
      fields: {
        billingDetails: {
          email:   this.email    ? "never" : "auto",
          phone:   this.telefono ? "never" : "auto",
          address: "auto",
        },
      },
    });

    this.paymentElement.mount("#stripe-payment-element");
    this.paymentElement.on("ready", () => { this.elementReady = true; });
    this.paymentElement.on("change", (e) => {
      if (e.complete) this.paymentError = null;
    });
  },
  beforeUnmount() {
    if (this.paymentElement) this.paymentElement.unmount();
  },
  methods: {
    async handlePayment() {
      this.isProcessing = true;
      this.paymentError = null;

      try {
        if (this.mode === "reactivar") {
          await this.handleReactivacion();
        } else if (this.mode === "actualizarpago") {
          await this.handleActualizarPago();
        } else if (this.isTrial) {
          await this.handleTrialSetup();
        } else {
          await this.handleRegularPayment();
        }
      } catch (err) {
        console.error("❌ Error en PaymentStripeElement:", err);
        showSwal.methods.showSwal({
          type: "error",
          message: this.$t("paymentStripe.paymentProcessingError"),
          width: 550,
        });
      } finally {
        this.isProcessing = false;
      }
    },

    async handleRegularPayment() {
      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: window.location.origin + "/pago/resultado" + (this.referencia ? `?ref=${encodeURIComponent(this.referencia)}` : ""),
          payment_method_data: {
            billing_details: {
              email:   this.email    || undefined,
              phone:   this.telefono || undefined,
            },
          },
        },
        // Suscripciones con setup_future_usage:off_session necesitan que Link
        // pueda autenticarse via redirección completa (en vez de en contexto)
        redirect: this.esSuscripcion ? "always" : "if_required",
      });

      if (error) {
        this.paymentError = error.message;
        showSwal.methods.showSwal({ type: "error", message: error.message });
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        showSwal.methods.showSwal({
          type: "success",
          message: this.$t("paymentStripe.paymentSuccess"),
          width: 550,
        });

        try {
          const response = await this.$store.dispatch("auth/confirmarPagado", {
            clientSecret: this.clientSecret,
            paymentId:    paymentIntent.id,
            email:        this.email,
            telefono:     this.telefono,
          });

          if (response.res === "OK") {
            this.$emit("cerrar-componente", response.res, response.enlace);
          } else {
            this.$emit("cerrar-componente", response.res, response.mensaje);
          }
        } catch (err) {
          console.error("❌ Backend falló tras pago OK:", err);
          showSwal.methods.showSwal({
            type: "warning",
            message: this.$t("paymentStripe.paymentConfirmedButError"),
            width: 600,
          });
        }
      } else if (paymentIntent && paymentIntent.status === "processing") {
        showSwal.methods.showSwal({
          type: "info",
          message: this.$t("paymentStripe.paymentProcessing"),
          width: 550,
        });
      } else {
        this.paymentError = this.$t("paymentStripe.paymentUnexpectedStatus");
      }
    },

    async handleTrialSetup() {
      const returnUrl = window.location.origin + "/pago/resultado"
        + (this.referencia ? "?ref=" + encodeURIComponent(this.referencia) : "");
      const { error } = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              email:   this.email    || undefined,
              phone:   this.telefono || undefined,
            },
          },
        },
        redirect: "always",
      });

      if (error) {
        this.paymentError = error.message;
        showSwal.methods.showSwal({ type: "error", message: error.message });
        return;
      }
      // Con redirect:"always" Stripe siempre redirige antes de resolver la Promise.
      // La confirmación del trial se gestiona en PagoResultado.vue.
    },

    // Modo reactivar: confirmPayment → emite paymentIntentId al padre (sin llamar al store)
    async handleReactivacion() {
      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: window.location.origin + "/panel-cliente",
          payment_method_data: {
            billing_details: { email: this.email || undefined },
          },
        },
        redirect: "if_required",
      });

      if (error) {
        this.paymentError = error.message;
        showSwal.methods.showSwal({ type: "error", message: error.message });
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        this.$emit("cerrar-componente", "OK", paymentIntent.id);
      } else if (paymentIntent && paymentIntent.status === "processing") {
        showSwal.methods.showSwal({
          type: "info",
          message: this.$t("paymentStripe.paymentProcessing"),
          width: 550,
        });
      } else {
        this.paymentError = this.$t("paymentStripe.paymentUnexpectedStatus");
      }
    },

    // Modo actualizarpago: confirmSetup → emite setupIntentId al padre (sin llamar al store)
    async handleActualizarPago() {
      const { error, setupIntent } = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          return_url: window.location.origin + "/panel-cliente",
          payment_method_data: {
            billing_details: { email: this.email || undefined },
          },
        },
        redirect: "if_required",
      });

      if (error) {
        this.paymentError = error.message;
        showSwal.methods.showSwal({ type: "error", message: error.message });
        return;
      }

      if (setupIntent && setupIntent.status === "succeeded") {
        this.$emit("cerrar-componente", "OK", setupIntent.id);
      } else {
        this.paymentError = this.$t("paymentStripe.paymentUnexpectedStatus");
      }
    },
  },
};
</script>

<style scoped>
.pago-container {
  max-width: 460px;
  padding: 2rem;
  border-radius: 14px;
  background: var(--q-card);
  border: 1px solid var(--q-border-md);
}

.titulo-pago {
  border: none !important;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--q-gold);
  margin-bottom: 0.5rem;
}

.payment-element-wrap {
  width: 100%;
}

#stripe-payment-element {
  width: 100%;
}

.error-stripe {
  color: var(--q-red);
  font-size: 0.9rem;
}

.btn-pagar span {
  font-weight: 600;
  font-size: 1rem;
  color: #fff;
}

.trial-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(40, 167, 69, 0.12);
  border: 1px solid rgba(40, 167, 69, 0.35);
  border-radius: 10px;
  padding: 10px 14px;
  margin-top: 0.75rem;
}
.trial-badge {
  flex-shrink: 0;
  background: var(--q-green, #28a745);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  border-radius: 6px;
  padding: 2px 8px;
}
.trial-desc {
  font-size: 0.88rem;
  color: var(--q-text, #e7edf5);
  line-height: 1.4;
}
.trial-note {
  font-size: 0.8rem;
  color: var(--q-muted, rgba(231,237,245,0.55));
}

@media (max-width: 600px) {
  .zona-pago { margin-top: 0.5rem !important; }
  .pago-container {
    padding: 1.25rem 1rem !important;
    border-radius: 10px !important;
    max-width: 100% !important;
  }
  .titulo-pago { font-size: 1rem !important; margin-bottom: 0.25rem !important; }
  .botones-accion { margin-top: 1rem !important; }
  .btn-pagar span { font-size: 0.95rem !important; }
  .trial-banner { flex-direction: column; align-items: flex-start; gap: 6px; padding: 8px 12px; }
}
</style>
